import { useState, useRef, useEffect, ReactNode } from "react";
import { getCategoryDisplayIconUrl } from "../utils/categoryDisplayIcons";
import { getProductDetailPath } from "../lib/productRoutes";
import {
  ChevronRight,
  Package,
  Wrench,
  Shirt,
  ShirtIcon,
  Car,
  Gem,
  Dumbbell,
  Laptop,
  Home as HomeIcon,
  Watch,
  Camera,
  Snowflake,
  Footprints,
  Sofa,
  Gamepad2,
  Book,
  Heart,
  UtensilsCrossed,
  Dog,
  Folder,
  Hammer,
  Cog,
  Scissors,
  FlaskConical,
  Box,
  Construction,
  Battery,
  Wheat,
  Stethoscope,
  Lock,
  Phone,
  Truck,
} from "lucide-react";

export interface Category {
  id: string;
  name: string;
  slug?: string;
  icon?: string;
  coverImage?: string;
  children?: Category[];
}

export interface CategoryMegaMenuProps {
  // Required props
  categories: Category[];
  
  // Mode: 'selection' for forms, 'navigation' for browsing
  mode: 'selection' | 'navigation';
  
  // Selection mode props
  selectedCategoryId?: string;
  onSelect?: (categoryId: string, categoryName: string) => void;
  
  // Navigation mode props
  onNavigate?: (path: string) => void;
  
  // Display variant
  variant?: 'fullscreen' | 'dropdown';
  
  // Custom trigger (for navigation mode)
  trigger?: ReactNode;
  
  // Optional features
  showProducts?: boolean;
  categorySubcategories?: { [key: string]: any[] };
  mockProducts?: any[];
  placeholderImage?: string;
  
  // Internal state control (optional)
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  
  // For navigation mode: get products for category
  getProductsForCategory?: (categoryId: string) => any[];
}

export const getCategoryIcon = (category: Category) => {
  const slug = category?.slug?.toLowerCase() || "";
  const name = category?.name?.toLowerCase() || "";

  const iconMap: { [key: string]: (props: { className?: string }) => any } = {
    "home-improvement": Wrench,
    "home improvement": Wrench,
    lingerie: Shirt,
    "womens-clothing": Shirt,
    "women's clothing": Shirt,
    "mens-clothing": ShirtIcon,
    "men's clothing": ShirtIcon,
    automotive: Car,
    jewelry: Gem,
    sports: Dumbbell,
    computer: Laptop,
    "home-garden": HomeIcon,
    "home & garden": HomeIcon,
    accessories: Watch,
    electronics: Camera,
    appliances: Snowflake,
    shoes: Footprints,
    furniture: Sofa,
    toys: Gamepad2,
    books: Book,
    health: Heart,
    food: UtensilsCrossed,
    pet: Dog,
    office: Folder,
    tools: Hammer,
    machinery: Cog,
    textiles: Scissors,
    chemicals: FlaskConical,
    packaging: Box,
    construction: Construction,
    energy: Battery,
    agriculture: Wheat,
    medical: Stethoscope,
    security: Lock,
    telecom: Phone,
    logistics: Truck,
  };

  return iconMap[slug] || iconMap[name] || Package;
};

// Flatten category children for display
const flattenCategoryChildren = (category: Category): Category[] => {
  const result: Category[] = [];

  if (category.children && category.children.length > 0) {
    category.children.forEach((level2Category) => {
      if (level2Category.children && level2Category.children.length > 0) {
        level2Category.children.forEach((level3Category) => {
          result.push(level3Category);
        });
      } else {
        result.push(level2Category);
      }
    });
  }

  return result;
};

export const CategoryMegaMenu = ({
  categories,
  mode = 'selection',
  selectedCategoryId,
  onSelect,
  onNavigate,
  variant = 'dropdown',
  trigger,
  showProducts = false,
  categorySubcategories,
  mockProducts = [],
  placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23e5e7eb' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='18' fill='%239ca3af'%3EImage%3C/text%3E%3C/svg%3E",
  controlledOpen,
  onOpenChange,
  getProductsForCategory,
}: CategoryMegaMenuProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [megaMenuHoveredCategory, setMegaMenuHoveredCategory] = useState<string | null>(null);
  
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const leftCategoryListRef = useRef<HTMLDivElement>(null);
  const rightContentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Use controlled or internal state
  const showMegaMenu = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setShowMegaMenu = (open: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(open);
    }
    onOpenChange?.(open);
  };

  // Get selected category path (for selection mode)
  const getSelectedCategoryPath = () => {
    if (!selectedCategoryId || mode !== 'selection') return null;

    const findCategoryPath = (
      cats: Category[],
      id: string,
      path: Category[] = []
    ): Category[] | null => {
      for (const cat of cats) {
        const currentPath = [...path, cat];
        if (cat.id === id) {
          return currentPath;
        }
        if (cat.children) {
          const found = findCategoryPath(cat.children, id, currentPath);
          if (found) return found;
        }
      }
      return null;
    };

    return findCategoryPath(categories, selectedCategoryId);
  };

  const selectedCategoryPath = getSelectedCategoryPath();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        megaMenuRef.current &&
        !megaMenuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShowMegaMenu(false);
        setMegaMenuHoveredCategory(null);
      }
    };

    if (showMegaMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showMegaMenu]);

  // Handle category selection/navigation
  const handleCategoryAction = (category: Category) => {
    if (mode === 'selection' && onSelect) {
      onSelect(category.id, category.name);
      setShowMegaMenu(false);
      setMegaMenuHoveredCategory(null);
    } else if (mode === 'navigation' && onNavigate) {
      if (category.children && category.children.length > 0) {
        onNavigate(`/categories/${category.slug || category.id}`);
      } else {
        onNavigate(`/products/category/${category.id}`);
      }
      setShowMegaMenu(false);
      setMegaMenuHoveredCategory(null);
    }
  };

  const handleSubcategoryAction = (
    subcategory: Category,
    parentCategory: Category
  ) => {
    if (mode === 'selection' && onSelect) {
      onSelect(subcategory.id, subcategory.name);
      setShowMegaMenu(false);
      setMegaMenuHoveredCategory(null);
    } else if (mode === 'navigation' && onNavigate) {
      onNavigate(`/products/category/${subcategory.id}`);
      setShowMegaMenu(false);
      setMegaMenuHoveredCategory(null);
    }
  };

  const handleProductSelect = (product: any) => {
    if (mode === 'navigation' && onNavigate) {
      onNavigate(getProductDetailPath(product));
      setShowMegaMenu(false);
      setMegaMenuHoveredCategory(null);
    }
  };

  // Scroll synchronization: Right scroll updates left active category
  useEffect(() => {
    if (
      !showMegaMenu ||
      !rightContentRef.current ||
      !leftCategoryListRef.current
    ) {
      return;
    }

    const rightContent = rightContentRef.current;
    let isScrolling = false;

    const handleRightScroll = () => {
      if (isScrolling) return;
      isScrolling = true;

      const categorySections = rightContent.querySelectorAll<HTMLElement>(
        "[data-category-section]"
      );

      let activeSection: HTMLElement | null = null;
      let minDistance = Infinity;

      categorySections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(
          rect.top - rightContent.getBoundingClientRect().top
        );
        if (distance < minDistance) {
          minDistance = distance;
          activeSection = section;
        }
      });

      if (activeSection) {
        const htmlElement = activeSection as HTMLElement;
        const categoryId = htmlElement.getAttribute("data-category-id");
        if (categoryId && categoryId !== megaMenuHoveredCategory) {
          setMegaMenuHoveredCategory(categoryId);

          const leftList = leftCategoryListRef.current;
          const activeItem = leftList?.querySelector(`#category-${categoryId}`);
          if (activeItem && leftList) {
            activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }
      }

      setTimeout(() => {
        isScrolling = false;
      }, 100);
    };

    rightContent.addEventListener("scroll", handleRightScroll);
    return () => {
      rightContent.removeEventListener("scroll", handleRightScroll);
    };
  }, [showMegaMenu, megaMenuHoveredCategory]);

  // Get products for a category (for navigation mode)
  const getSampleProductsForCategory = (categoryId: string) => {
    if (getProductsForCategory) {
      return getProductsForCategory(categoryId);
    }
    const filtered = mockProducts.filter((p) => p.categoryId === categoryId);
    return filtered.slice(0, 30);
  };

  // Get subcategories for a category
  const getSubcategoriesForCategory = (categoryId: string) => {
    if (categorySubcategories && categorySubcategories[categoryId]) {
      return categorySubcategories[categoryId];
    }
    const category = categories.find((c) => c.id === categoryId);
    if (category) {
      return flattenCategoryChildren(category);
    }
    return [];
  };

  const menuContent = (
    <div className="flex h-full" style={{ maxHeight: variant === 'fullscreen' ? 'calc(80vh - 100px)' : '600px' }}>
      {/* Left Sidebar - All Categories */}
      <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <h3 className="text-lg font-semibold mb-4 px-4 pt-4 text-gray-900 flex-shrink-0">
          All Categories
        </h3>
        <div
          ref={leftCategoryListRef}
          className="flex-1 overflow-y-auto category-scrollbar"
          style={{ overscrollBehavior: "contain" }}
        >
          <div className="space-y-1 pb-2 pr-2">
            {categories.map((category) => {
              const isHovered = megaMenuHoveredCategory === category.id;
              const isSelected =
                mode === 'selection' &&
                selectedCategoryPath?.some((cat) => cat.id === category.id);
              const IconComponent = getCategoryIcon(category);
              const displayIconUrl = getCategoryDisplayIconUrl(category.name);
              return (
                <div
                  key={category.id}
                  id={`category-${category.id}`}
                  onMouseEnter={() => setMegaMenuHoveredCategory(category.id)}
                  onClick={() => handleCategoryAction(category)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    isHovered || isSelected
                      ? "bg-white text-orange-600 font-medium border-r-2 border-orange-600"
                      : "text-gray-700 hover:bg-white"
                  }`}
                >
                  {displayIconUrl ? (
                    <img
                      src={displayIconUrl}
                      alt=""
                      className="h-5 w-5 flex-shrink-0 object-contain"
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <IconComponent
                      className={`h-5 w-5 flex-shrink-0 ${
                        isHovered || isSelected
                          ? "text-orange-600"
                          : "text-gray-700"
                      }`}
                    />
                  )}
                  <span className="text-sm flex-1">{category.name}</span>
                  <ChevronRight
                    className={`h-4 w-4 transition-transform ${
                      isHovered || isSelected
                        ? "text-orange-600"
                        : "text-gray-400"
                    }`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Category Content */}
      <div
        ref={rightContentRef}
        className="flex-1 min-w-0 overflow-y-auto bg-white category-scrollbar"
        style={{
          overscrollBehavior: "contain",
          maxHeight: variant === 'fullscreen' ? 'calc(80vh - 100px)' : '600px',
        }}
      >
        {megaMenuHoveredCategory ? (
          <div className={variant === 'fullscreen' ? "py-6" : "py-6 pl-6 pr-4"}>
            <h3
              className="text-2xl font-semibold mb-6 text-gray-900 cursor-pointer hover:text-orange-600 transition-colors"
              onClick={() => {
                const category = categories.find(
                  (c) => c.id === megaMenuHoveredCategory
                );
                if (category) {
                  handleCategoryAction(category);
                }
              }}
            >
              {categories.find((c) => c.id === megaMenuHoveredCategory)?.name}
            </h3>
            {(() => {
              const subcategories = getSubcategoriesForCategory(megaMenuHoveredCategory);

              if (subcategories.length > 0) {
                return (
                  <div
                    data-category-section
                    data-category-id={megaMenuHoveredCategory}
                  >
                    <div className={variant === 'fullscreen' ? "grid grid-cols-8 gap-4" : "grid grid-cols-6 gap-4"}>
                      {subcategories.map((subcategory, idx) => (
                        <div
                          key={subcategory.id || idx}
                          onClick={() => {
                            const parentCategory = categories.find(
                              (c) => c.id === megaMenuHoveredCategory
                            );
                            if (parentCategory) {
                              handleSubcategoryAction(subcategory, parentCategory);
                            }
                          }}
                          className={`flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity group ${
                            mode === 'selection' && selectedCategoryId === subcategory.id
                              ? "ring-2 ring-orange-500 rounded-lg p-1"
                              : ""
                          }`}
                        >
                          <div className={`${variant === 'fullscreen' ? 'w-20 h-20' : 'w-16 h-16'} rounded-full bg-gray-100 mb-2 overflow-hidden flex items-center justify-center border border-gray-200 group-hover:border-orange-500 transition-colors`}>
                            {subcategory.coverImage || subcategory.icon ? (
                              <img
                                src={subcategory.coverImage || subcategory.icon}
                                alt={subcategory.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = placeholderImage;
                                }}
                              />
                            ) : (
                              <img
                                src={placeholderImage}
                                alt={subcategory.name}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <span className="text-xs text-gray-700 text-center line-clamp-2 group-hover:text-orange-600 transition-colors">
                            {subcategory.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              } else if (showProducts && mode === 'navigation') {
                const products = getSampleProductsForCategory(megaMenuHoveredCategory);
                if (products.length > 0) {
                  return (
                    <div
                      data-category-section
                      data-category-id={megaMenuHoveredCategory}
                    >
                      <h4 className="font-semibold text-gray-900 mb-4 text-lg">Products</h4>
                      <div className="grid grid-cols-7 gap-4">
                        {products.map((product) => (
                          <div
                            key={product.id}
                            className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleProductSelect(product)}
                          >
                            <div className="w-20 h-20 rounded-full bg-gray-100 mb-2 overflow-hidden flex items-center justify-center">
                              <img
                                src={product.image || placeholderImage}
                                alt={product.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span className="text-xs text-gray-700 text-center line-clamp-2">
                              {product.title?.split(" - ")[0] || product.title}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              }
              
              return (
                <div className="text-center py-8">
                  <p className="text-gray-400 text-sm">
                    {mode === 'selection'
                      ? "No subcategories available. Click the category name to select."
                      : "No subcategories available."}
                  </p>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <p className="text-gray-400 text-sm">
              Hover over a category to see {mode === 'selection' ? 'subcategories' : 'products'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const menuContainerClasses = variant === 'fullscreen'
    ? "fixed top-[100px] left-0 right-0 bg-white z-50"
    : "absolute top-full left-0 right-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] overflow-hidden";

  return (
    <>
      <style>{`
        .category-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .category-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .category-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .category-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          border: 2px solid #f1f5f9;
        }
        .category-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .category-scrollbar.bg-white::-webkit-scrollbar-track {
          background: #ffffff;
        }
        .category-scrollbar.bg-white::-webkit-scrollbar-thumb {
          border: 2px solid #ffffff;
        }
      `}</style>
      
      <div className="relative w-full" ref={triggerRef}>
        {trigger && (
          <div onClick={() => setShowMegaMenu(!showMegaMenu)}>
            {trigger}
          </div>
        )}

        {showMegaMenu && (
          <>
            {variant === 'fullscreen' && (
              <>
                {/* Background Blur Overlay */}
                <div
                  className="fixed top-[100px] left-0 right-0 bottom-0 bg-black/20 backdrop-blur-sm z-40"
                  onClick={() => {
                    setShowMegaMenu(false);
                    setMegaMenuHoveredCategory(null);
                  }}
                />
                {/* Bottom Clickable Area */}
                <div
                  className="fixed bottom-0 left-0 right-0 bg-transparent z-50 cursor-pointer"
                  style={{ height: "20vh" }}
                  onClick={() => {
                    setShowMegaMenu(false);
                    setMegaMenuHoveredCategory(null);
                  }}
                />
              </>
            )}
            
            {/* Main Panel */}
            <div
              ref={megaMenuRef}
              className={menuContainerClasses}
              style={variant === 'fullscreen' ? { height: "calc(80vh - 100px)" } : { maxHeight: "600px" }}
              onClick={(e) => e.stopPropagation()}
            >
              {variant === 'fullscreen' ? (
                <div className="w-full h-full py-6">
                  <div className="max-w-7xl mx-auto px-4 h-full mega-menu-container">
                    {menuContent}
                  </div>
                </div>
              ) : (
                menuContent
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

